import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Mail, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Referral {
  id: string;
  provider_name: string;
  provider_email: string;
  context_tags: string[];
  status: 'pending' | 'accepted' | 'declined';
  date_initiated: string;
  outcome_notes?: string;
}

const MyReferrals = () => {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  useEffect(() => {
    fetchReferrals();
  }, [user]);

  const fetchReferrals = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('id, provider_name, provider_email, context_tags, status, date_initiated, outcome_notes')
        .eq('referring_user', user.id)
        .order('date_initiated', { ascending: false });

      if (error) throw error;
      setReferrals((data || []).map(item => ({
        ...item,
        status: item.status as 'pending' | 'accepted' | 'declined'
      })));
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const updateReferralStatus = async (referralId: string, status: string, notes?: string) => {
    setUpdatingStatus(referralId);

    try {
      const { error } = await supabase
        .from('referrals')
        .update({ 
          status,
          outcome_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', referralId);

      if (error) throw error;

      setReferrals(prev => 
        prev.map(ref => 
          ref.id === referralId 
            ? { ...ref, status: status as any, outcome_notes: notes }
            : ref
        )
      );

      toast.success(`Referral marked as ${status}`);
    } catch (error) {
      console.error('Error updating referral:', error);
      toast.error('Failed to update referral status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'declined':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-orange-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'declined':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-orange-100 text-orange-800 border-orange-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">My Referrals</h2>
        <Badge variant="outline" className="text-sm">
          {referrals.length} Total Referrals
        </Badge>
      </div>

      {referrals.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Referrals Yet</h3>
            <p className="text-muted-foreground">
              Your submitted referrals will appear here for tracking and status updates.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {referrals.map((referral) => (
            <ReferralCard
              key={referral.id}
              referral={referral}
              onUpdateStatus={updateReferralStatus}
              isUpdating={updatingStatus === referral.id}
              getStatusIcon={getStatusIcon}
              getStatusColor={getStatusColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ReferralCardProps {
  referral: Referral;
  onUpdateStatus: (id: string, status: string, notes?: string) => void;
  isUpdating: boolean;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusColor: (status: string) => string;
}

const ReferralCard = ({ referral, onUpdateStatus, isUpdating, getStatusIcon, getStatusColor }: ReferralCardProps) => {
  const [notes, setNotes] = useState(referral.outcome_notes || '');
  const [showNotes, setShowNotes] = useState(false);

  const handleStatusUpdate = async (status: string) => {
    if (status !== 'pending' && showNotes) {
      await onUpdateStatus(referral.id, status, notes);
      setShowNotes(false);
    } else if (status !== 'pending') {
      setShowNotes(true);
    } else {
      await onUpdateStatus(referral.id, status);
    }
  };

  const isClosed = referral.status === 'accepted' || referral.status === 'declined';

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg">{referral.provider_name}</h3>
              {getStatusIcon(referral.status)}
            </div>
            <p className="text-sm text-muted-foreground">{referral.provider_email}</p>
            <p className="text-sm text-muted-foreground">
              Initiated: {format(new Date(referral.date_initiated), 'MMM d, yyyy')}
            </p>
          </div>
          
          <Badge variant="outline" className={getStatusColor(referral.status)}>
            {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
          </Badge>
        </div>

        {/* Context Tags */}
        <div className="mb-4">
          <p className="text-sm font-medium mb-2">Support Areas:</p>
          <div className="flex flex-wrap gap-2">
            {referral.context_tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </div>

        {/* Outcome Notes */}
        {referral.outcome_notes && (
          <div className="mb-4 p-3 bg-secondary/20 rounded-lg">
            <p className="text-sm font-medium mb-1">Outcome Notes:</p>
            <p className="text-sm text-muted-foreground">{referral.outcome_notes}</p>
          </div>
        )}

        {/* Status Update Section */}
        {!isClosed && (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusUpdate('accepted')}
                disabled={isUpdating}
                className="text-green-700 border-green-200 hover:bg-green-50"
              >
                Mark Accepted
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusUpdate('declined')}
                disabled={isUpdating}
                className="text-red-700 border-red-200 hover:bg-red-50"
              >
                Mark Declined
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusUpdate('pending')}
                disabled={isUpdating}
              >
                Mark Pending
              </Button>
            </div>

            {showNotes && (
              <div className="space-y-3">
                <Alert className="border-warning bg-warning/10">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertDescription className="text-warning-foreground text-sm">
                    Do not include PHI/PII in outcome notes.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Outcome Notes (optional):</label>
                  <Textarea
                    placeholder="General notes about the referral outcome (no PHI/PII)..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={140}
                    className="resize-none"
                  />
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-destructive">No PHI/PII allowed</span>
                    <span className="text-muted-foreground">{notes.length}/140</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(referral.id, 'accepted', notes)}
                    disabled={isUpdating}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Save as Accepted
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(referral.id, 'declined', notes)}
                    disabled={isUpdating}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Save as Declined
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowNotes(false)}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyReferrals;